import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Switch,
  Stack,
  alpha,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Add, Edit, Delete, Gavel } from "@mui/icons-material";
import { useMutation } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { DataTableShell } from "../components/table/DataTableShell";
import { CountingFilterSelect } from "../components/table/CountingFilterSelect";

type RuleType = "categorization" | "matching" | "vendor_alias";

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  categorization: "Categorization",
  matching: "Matching",
  vendor_alias: "Vendor Alias",
};

const RULE_TYPE_COLORS: Record<RuleType, "primary" | "secondary" | "info"> = {
  categorization: "primary",
  matching: "secondary",
  vendor_alias: "info",
};

interface RulesColumnHandlers {
  onEdit: (rule: any) => void;
  onDelete: (rule: any) => void;
  onToggleActive: (rule: any) => void;
}

function buildColumns({ onEdit, onDelete, onToggleActive }: RulesColumnHandlers): GridColDef[] {
  return [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      minWidth: 160,
      sortable: true,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 150,
      sortable: true,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={RULE_TYPE_LABELS[p.value as RuleType] ?? p.value}
          color={RULE_TYPE_COLORS[p.value as RuleType] ?? "default"}
          size="small"
        />
      ),
    },
    {
      field: "pattern",
      headerName: "Pattern",
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box
          component="code"
          sx={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: "0.75rem",
            px: 1,
            py: 0.5,
            borderRadius: 1,
            background: (theme) => alpha(theme.palette.primary.main, 0.08),
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            color: "var(--c-link-accent)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }}
          title={p.value as string}
        >
          {p.value}
        </Box>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      sortable: true,
      valueGetter: (_v, row) => row.category || row.vendorCanonical || "—",
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 100,
      sortable: true,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "matchCount",
      headerName: "Matches",
      width: 110,
      sortable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color:
              (p.value as number) > 0
                ? "var(--c-metric-success)"
                : "text.secondary",
          }}
        >
          {p.value ?? 0}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 90,
      sortable: true,
      renderCell: (p: GridRenderCellParams) => (
        <Switch
          checked={p.value as boolean}
          onChange={(e) => {
            e.stopPropagation();
            onToggleActive(p.row as any);
          }}
          onClick={(e) => e.stopPropagation()}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(p.row as any);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(p.row as any);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];
}

export function RulesEngine() {
  const rules = useStableQuery(api.rules.listAll, {}, []);
  const createRule = useMutation(api.rules.create);
  const updateRule = useMutation(api.rules.update);
  const removeRule = useMutation(api.rules.remove);

  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<any | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState<any | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Form state
  const [formName, setFormName] = useState("");
  const [formPattern, setFormPattern] = useState("");
  const [formType, setFormType] = useState<RuleType>("categorization");
  const [formCategory, setFormCategory] = useState("");
  const [formVendorCanonical, setFormVendorCanonical] = useState("");
  const [formPriority, setFormPriority] = useState<number>(10);

  const allRules: any[] = rules ?? [];

  const typeCounts = useMemo(() => {
    const counts: Record<RuleType, number> = {
      categorization: 0,
      matching: 0,
      vendor_alias: 0,
    };
    for (const r of allRules) {
      if (counts[r.type as RuleType] !== undefined) counts[r.type as RuleType]++;
    }
    return counts;
  }, [allRules]);

  const filtered =
    typeFilter === "all"
      ? allRules
      : allRules.filter((r: any) => r.type === typeFilter);

  const rows = filtered.map((r: any) => ({ ...r, id: r._id }));

  const openCreateDialog = () => {
    setEditingRule(null);
    setFormName("");
    setFormPattern("");
    setFormType("categorization");
    setFormCategory("");
    setFormVendorCanonical("");
    setFormPriority(10);
    setDialogOpen(true);
  };

  const openEditDialog = useCallback((rule: any) => {
    setEditingRule(rule);
    setFormName(rule.name);
    setFormPattern(rule.pattern);
    setFormType(rule.type);
    setFormCategory(rule.category || "");
    setFormVendorCanonical(rule.vendorCanonical || "");
    setFormPriority(rule.priority ?? 10);
    setDialogOpen(true);
  }, []);

  const handleSave = async () => {
    try {
      if (editingRule) {
        await updateRule({
          id: editingRule._id,
          name: formName,
          pattern: formPattern,
          category: formType === "categorization" ? formCategory : undefined,
          vendorCanonical: formType === "vendor_alias" ? formVendorCanonical : undefined,
          priority: formPriority,
        } as any);
        setSnackbar({ open: true, message: "Rule updated successfully", severity: "success" });
      } else {
        await createRule({
          type: formType,
          name: formName,
          pattern: formPattern,
          category: formType === "categorization" ? formCategory : undefined,
          vendorCanonical: formType === "vendor_alias" ? formVendorCanonical : undefined,
          priority: formPriority,
        } as any);
        setSnackbar({ open: true, message: "Rule created successfully", severity: "success" });
      }
      setDialogOpen(false);
    } catch (err: any) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: "error" });
    }
  };

  const handleToggleActive = useCallback(
    async (rule: any) => {
      try {
        await updateRule({ id: rule._id, isActive: !rule.isActive } as any);
      } catch (err: any) {
        setSnackbar({ open: true, message: `Error toggling rule: ${err.message}`, severity: "error" });
      }
    },
    [updateRule],
  );

  const openDeleteConfirm = useCallback((rule: any) => {
    setDeletingRule(rule);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!deletingRule) return;
    try {
      await removeRule({ id: deletingRule._id } as any);
      setSnackbar({ open: true, message: "Rule deleted successfully", severity: "success" });
      setDeleteConfirmOpen(false);
      setDeletingRule(null);
    } catch (err: any) {
      setSnackbar({ open: true, message: `Error deleting rule: ${err.message}`, severity: "error" });
    }
  };

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit: openEditDialog,
        onDelete: openDeleteConfirm,
        onToggleActive: handleToggleActive,
      }),
    [openEditDialog, openDeleteConfirm, handleToggleActive],
  );

  return (
    <>
      <DataTableShell
        title="Rules Engine"
        subtitle="Manage categorization, matching, and vendor alias rules for transaction processing"
        rows={rows}
        columns={columns}
        loading={rules === undefined}
        totalBeforeFilters={allRules.length}
        itemLabel="rule"
        emptyTitle="No rules configured"
        emptyDescription="Click 'Add Rule' to create your first categorization, matching, or vendor alias rule."
        emptyIcon={<Gavel sx={{ fontSize: 32, color: "var(--c-link-accent)" }} />}
        onResetFilters={() => setTypeFilter("all")}
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "priority", sort: "asc" }] },
          },
          // Click anywhere on the row to open the edit dialog. Action buttons
          // and the toggle stop propagation so they fire their own handlers.
          onRowClick: (params) => openEditDialog(params.row),
          sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
        }}
        headerExtra={
          <Box
            sx={{
              p: 1.5,
              borderBottom: "1px solid var(--c-overlay-6)",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Add Rule
            </Button>
          </Box>
        }
        filterBar={
          <CountingFilterSelect
            label="Type"
            value={typeFilter}
            onChange={setTypeFilter}
            allLabel="All Types"
            options={[
              { value: "categorization", label: "Categorization", count: typeCounts.categorization },
              { value: "matching", label: "Matching", count: typeCounts.matching },
              { value: "vendor_alias", label: "Vendor Alias", count: typeCounts.vendor_alias },
            ]}
          />
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {editingRule ? "Edit Rule" : "Create Rule"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {editingRule
              ? "Update the rule configuration below"
              : "Define a new rule for automated transaction processing"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formType}
                label="Type"
                onChange={(e) => setFormType(e.target.value as RuleType)}
                disabled={!!editingRule}
              >
                <MenuItem value="categorization">Categorization</MenuItem>
                <MenuItem value="matching">Matching</MenuItem>
                <MenuItem value="vendor_alias">Vendor Alias</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Pattern"
              value={formPattern}
              onChange={(e) => setFormPattern(e.target.value)}
              required
              fullWidth
              helperText="Regex or keyword pattern to match against transaction descriptions"
              InputProps={{ sx: { fontFamily: "'IBM Plex Mono', monospace" } }}
            />
            {formType === "categorization" && (
              <TextField
                label="Category"
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                fullWidth
              />
            )}
            {formType === "vendor_alias" && (
              <TextField
                label="Vendor Canonical Name"
                value={formVendorCanonical}
                onChange={(e) => setFormVendorCanonical(e.target.value)}
                fullWidth
              />
            )}
            <TextField
              label="Priority"
              type="number"
              value={formPriority}
              onChange={(e) => setFormPriority(Number(e.target.value))}
              fullWidth
              helperText="Lower number = higher priority"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formName || !formPattern}>
            {editingRule ? "Update Rule" : "Create Rule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Delete Rule?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete rule &apos;{deletingRule?.name}&apos;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
